import {
  Breadcrumbs,
  ButtonGroup,
  CircularProgress,
  Divider,
  Dropdown,
  IconButton,
  ListSubheader,
  Menu,
  MenuButton,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/joy';
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  CaretDown,
  Columns,
  DownloadSimple,
  FileArrowDown,
  Folder as FolderIcon,
  GridFour,
  MinusSquare,
  PlusSquare,
  Trash,
  UploadSimple,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { StatusCodes } from 'http-status-codes';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import AttachedFileItemDisplay from './casemanagerAttachments/card/AttachedFileItemDisplay';
import CreateFolderModal from './casemanagerAttachments/modal/CreateFolderModal';
import MoveFolderModal from './casemanagerAttachments/modal/MoveFolderModal';
import OtherOptionsPopoverFile from './casemanagerAttachments/popover/OtherOptionsPopoverFile';
import OtherOptionPopoverFolder from './casemanagerAttachments/popover/OtherOptionsPopoverFolder';
import { auditLog, downloadFile, findItemAndParent, getFileExtension } from './utils/utils';

import noAttachmentIcon from '../../../../../assets/svg/attachments.svg';

import { isRESTAPIError, setS3UploadFormData } from '~/assets/utils/helpers';
import {
  generateFileUploadErrorResponse,
  generateFileUploadToS3BucketErrorResponse,
} from '~/assets/utils/responseFactory';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import { Text } from '~/components/ui/text';
import { CasemanagerAttachment } from '~/constants/reportConstants';
import { useAuth } from '~/contexts/auth/authContext';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import RESTAPIError from '~/interfaces/RESTAPIError';
import { ReportService } from '~/services/api/reports/endpoints';
import {
  AttachmentFile,
  AttachmentFolder,
  ReportAttachementProps,
} from '~/services/api/reports/types';
import { isMutationSuccess } from '~/services/api/rest-api';
import reportFileService from '~/services/reportFileService';
import reportService from '~/services/reportService';
import { RootState } from '~/utils/store';

export type FileOrFolder = AttachmentFile | AttachmentFolder;

const schema = z.object({
  files: z
    .array(
      z
        .instanceof(File)
        .refine(
          (file) => file.size <= CasemanagerAttachment.MAX_FILE_SIZE,
          (file) => ({
            message: `file: ${file.name} size exceeds the 1 GB limit.`,
          }),
        )
        .refine(
          (file) =>
            CasemanagerAttachment.ALLOWED_EXTENSIONS.includes(
              getFileExtension(file),
            ),
          (file) => ({
            message: `file: ${file.name} - ${getFileExtension(file)} type is not allowed.`,
          }),
        ),
    )
    .nonempty({ message: 'No files selected for upload.' }),
});
export interface DirectoryPathProps {
  id: number | null;
  name: string;
}

export default function TabDisplay({
  item: attachments,
  mutationToRefetch,
}: {
  item: AttachmentFolder;
  mutationToRefetch: () => void;
}) {
  const [deleteFolder] =
    ReportService.endpoints.deleteFolderCasemanager.useMutation();
  const [deleteFile] =
    ReportService.endpoints.deleteFileCasemanager.useMutation();
  const userAccessRoles = useSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );
  const reportDetails = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const { slug } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reportId = useParams<{ reportId: string }>().reportId;
  const { openConfirmationModal, createToastNotification } =
    useToastNotificationContext();
  const intl = useIntl();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);

  const [attachmentsDisplay, setAttachmentsDisplay] = useState<FileOrFolder[]>(
    [],
  );

  const [directoryPath, setDirectoryPath] = useState<DirectoryPathProps[]>();

  const [plus, setPlus] = useState(true);
  const [gridView, setGridView] = useState(true);
  const [ascendingName, setAscendingName] = useState<boolean | null>(false);
  const [ascendingDateModified, setAscendingDateModified] = useState<
    boolean | null
  >(null);
  const [gridViewSortOption, setGridViewSortOption] = useState(true);

  const [showBulkUpdateButtons, setShowBulkUpdateButtons] = useState({
    download: false,
    remove: false,
    move: false,
  });

  const [isLoadingUpload, setIsLoadingUpload] = useState(false);

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const uploadFileStart = async (file: File) => {
    try {
      const { name } = file;
      return await reportFileService.customerUploadFileStart({
        fileName: name,
        fileType: getFileExtension(file),
        fileSize: file.size,
        targetFolder: directoryPath?.slice(-1)[0].id || undefined,
      });
    } catch (err: unknown) {
      let errorMessage = 'Error happened.';
      if (isRESTAPIError(err)) {
        errorMessage = generateFileUploadErrorResponse(err.response.status);
      }
      createToastNotification(errorMessage, 'error');
      throw err;
    }
  };

  const uploadFileActual = async (
    url: string,
    requestBody: FormData,
    name: string,
  ) => {
    try {
      return await reportFileService.uploadFileToS3Bucket({
        url,
        data: requestBody,
      });
    } catch (err: unknown) {
      let errorMessage = 'Error happened';
      if (isRESTAPIError(err)) {
        errorMessage = generateFileUploadToS3BucketErrorResponse(
          err.response.status,
          name,
        );
      }
      createToastNotification(errorMessage, 'error');
      throw err;
    }
  };

  const uploadFileFinish = async (id: number, name: string) => {
    try {
      return await reportFileService.customerUploadFileFinish({ fileId: id });
    } catch (err: unknown) {
      let errorMessage = 'Error happened';
      if (isRESTAPIError(err)) {
        errorMessage = generateFileUploadErrorResponse(
          err.response.status,
          name,
        );
      }
      createToastNotification(errorMessage, 'error');
      throw err;
    }
  };

  const uploadFile = async (file: File) => {
    const body = await uploadFileStart(file);
    const { url, id = null, fields } = body;
    const requestBody = { ...fields, ...{ file } };
    const requestBodyForS3 = setS3UploadFormData(requestBody);
    await uploadFileActual(url, requestBodyForS3, file.name);
    await uploadFileFinish(id, file.name);
    mutationToRefetch();
    return id;
  };

  const processFiles = async (files: File[]) => {
    const validFiles: File[] = files;

    setIsLoadingUpload(true);
    const validated = await schema.safeParseAsync({ files });
    if (!validated.success) {
      createToastNotification(
        validated.error.formErrors.fieldErrors.files?.join(',') ?? 'Error',
        'error',
      );
      return setIsLoadingUpload(false);
    }

    // Proceed with uploading valid files
    if (validFiles.length === 0) {
      setIsLoadingUpload(false);
      return createToastNotification('No files selected for upload.', 'error');
    }

    await Promise.all(
      validFiles.map(async (file) => {
        const resultFileUpload = await uploadFile(file);
        const [uploadResult] = await Promise.allSettled([resultFileUpload]);

        if (uploadResult.status === 'fulfilled') {
          await reportService.submitCustomerAdditionalInformation({
            reportId: reportDetails.reportId,
            fileIds: [resultFileUpload],
          });
          return resultFileUpload;
        } else {
          createToastNotification(
            `Error submitting the report: ${uploadResult.reason}`,
            'error',
          );
          throw new Error(
            `Error submitting the report: ${uploadResult.reason}`,
          );
        }
      }),
    );
    createToastNotification(
      intl.formatMessage({
        defaultMessage: 'Successfully uploaded the file.',
      }),
      'success',
    );

    setIsLoadingUpload(false);
  };

  // Download attachment
  const addAuditLog = async (name: string) => {
    try {
      if (reportId && companySlug) {
        const res = await auditLog({
          name,
          companySlug,
          reportId,
        });
        return res;
      } else {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Unauthorized to Audit.',
          }),
          'error',
        );
      }
    } catch (err) {
      if (isRESTAPIError(err)) {
        if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Error happened. We are currently fixing it.',
            }),
            'error',
          );
        } else if (
          `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
        ) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Failure to process request',
            }),
            'error',
          );
        } else if (
          `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
        ) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'You are unauthorized to update report',
            }),
            'error',
          );
        } else if (`${err.response.status}` === String(StatusCodes.FORBIDDEN)) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'You cannot use this request',
            }),
            'error',
          );
        } else if (
          `${err.response.status}` === String(StatusCodes.INTERNAL_SERVER_ERROR)
        ) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Error happened in the server',
            }),
            'error',
          );
        }
      }
    }
  };

  const downloadAttachment = async ({
    url,
    name,
  }: {
    url: string;
    name: string;
  }) => {
    if (userAccessRoles.is_allowed_to_download_attachments) {
      try {
        await downloadFile({
          url,
          name,
        });
        if (reportId) addAuditLog(name);
      } catch (error) {
        const apiError = error as RESTAPIError;
        throw new Error(apiError.message);
      }
    } else {
      throw new Error('Error downloading file.');
    }
  };

  useEffect(() => {
    const rootFolder = findItemAndParent(attachments, null);
    if (rootFolder.item) {
      setDirectoryPath([{ id: rootFolder.item?.id, name: 'All Files' }]);
    }
  }, [attachments]);

  useEffect(() => {
    if (directoryPath !== undefined && directoryPath.length > 0) {
      const currentFolderId = directoryPath[directoryPath.length - 1].id;
      const addDirectory = findItemAndParent(attachments, currentFolderId);

      if (addDirectory.item) {
        const flattenDisplayList = addDirectory.item.items.map((rest) => ({
          ...rest,
          items: [],
          is_checked: false,
        }));

        let sortedFolders = flattenDisplayList.filter((item) => item.is_folder);
        let sortedFiles = flattenDisplayList.filter((item) => !item.is_folder);

        if (ascendingName !== null) {
          sortedFolders = sortedFolders.sort((a, b) =>
            ascendingName
              ? b.name.localeCompare(a.name)
              : a.name.localeCompare(b.name),
          );
          sortedFiles = sortedFiles.sort((a, b) =>
            ascendingName
              ? b.name.localeCompare(a.name)
              : a.name.localeCompare(b.name),
          );
        } else if (ascendingDateModified !== null) {
          sortedFolders = sortedFolders.sort((a, b) =>
            ascendingDateModified
              ? new Date(b.last_modified).getTime() -
                new Date(a.last_modified).getTime()
              : new Date(a.last_modified).getTime() -
                new Date(b.last_modified).getTime(),
          );
          sortedFiles = sortedFiles.sort((a, b) =>
            ascendingDateModified
              ? new Date(b.last_modified).getTime() -
                new Date(a.last_modified).getTime()
              : new Date(a.last_modified).getTime() -
                new Date(b.last_modified).getTime(),
          );
        }

        setAttachmentsDisplay([...sortedFolders, ...sortedFiles]);
      }
    }
  }, [directoryPath, ascendingName, ascendingDateModified]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const showBulkButtons = attachmentsDisplay.some(
      (attachment) => attachment.is_checked === true,
    );

    const allNotFolder = attachmentsDisplay
      .filter((attachment) => attachment.is_checked === true)
      .every((attachment) => !attachment.is_folder);

    setShowBulkUpdateButtons({
      download: showBulkButtons && allNotFolder,
      remove: showBulkButtons,
      move: showBulkButtons,
    });

    const allChecked = attachmentsDisplay.every(
      (attachment) => attachment.is_checked === true,
    );

    setPlus(!allChecked);
  }, [attachmentsDisplay]);

  const handleNavigate = (folderId: number | null) => {
    const index = directoryPath?.findIndex((i) => i.id === folderId);

    if (index !== undefined && index !== -1) {
      setDirectoryPath(directoryPath?.slice(0, index + 1));
    }
  };

  const handleNavigateFolder = (folderId: number | null) => {
    const findItem = findItemAndParent(attachments, folderId);

    if (findItem.item) {
      const newItem: DirectoryPathProps = {
        id: findItem.item.id,
        name: findItem.item.name,
      };

      setDirectoryPath((prev) => [...(prev || []), newItem]);
    }
  };

  const handleClickCheckAll = () => {
    setAttachmentsDisplay((prev) =>
      prev.map((attachment) => ({ ...attachment, is_checked: plus })),
    );
    setPlus((value) => !value); // Toggle the value of 'plus' when the button is clicked
  };

  const handleClickGridView = () => {
    setGridView((value) => !value); // Toggle the value of 'plus' when the button is clicked
  };

  // Sort by name functions
  const handleSortByName = () => {
    if (ascendingName !== null) {
      setAscendingName((value) => !value);
    } else {
      setAscendingDateModified(null);
      setAscendingName(true);
    }
  };

  const handleSortByDateModified = () => {
    if (ascendingDateModified !== null) {
      setAscendingDateModified((value) => !value);
    } else {
      setAscendingName(null);
      setAscendingDateModified(true);
    }
  };

  const sortProperty = gridViewSortOption ? 'name' : 'last_modified';
  const getSortPropertyOrder = (property: 'name' | 'last_modified') => {
    if (property === 'name') {
      return ascendingName ? 'asc' : 'desc';
    } else if (property === 'last_modified') {
      return ascendingDateModified ? 'asc' : 'desc';
    }
    return 'asc';
  };
  const sortBy = (property: 'name' | 'last_modified') => {
    if (property === 'name') {
      handleSortByName();
    } else if (property === 'last_modified') {
      handleSortByDateModified();
    }
  };

  // Check item functions
  const handleCheckItem = (e: MouseEvent<HTMLSpanElement>, id: number) => {
    e.stopPropagation();
    const updatedAttachments = attachmentsDisplay.map((checkedItem) => {
      if (checkedItem.id === id) {
        return { ...checkedItem, is_checked: !checkedItem.is_checked };
      }
      return checkedItem;
    });

    setAttachmentsDisplay(updatedAttachments);
  };

  // Check all items functions
  const deleteFolderAction = (
    items: {
      parentFolder?: number;
      fileId: number;
      isFolder: boolean;
    }[],
  ) => {
    items.forEach(
      async (attachment: {
        parentFolder?: number;
        fileId: number;
        isFolder: boolean;
      }) => {
        if (attachment.isFolder) {
          const result = await deleteFolder({
            folder_id: attachment.fileId,
          });
          if (!isMutationSuccess(result)) {
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Something went wrong. Please try again later.',
              }),
              'error',
            );
          }
        } else {
          const result = await deleteFile({
            file_id: attachment.fileId,
            parent_folder: attachment.parentFolder ?? attachment.fileId,
          });
          if (!isMutationSuccess(result)) {
            createToastNotification(
              intl.formatMessage({
                defaultMessage: 'Something went wrong. Please try again later.',
              }),
              'error',
            );
          }
        }
      },
    );
    createToastNotification(
      intl.formatMessage({
        defaultMessage: 'Successfully deleted the file.',
      }),
      'success',
    );
  };

  const handleDeleteCheckedItems = () => {
    const filteredAttachments = attachmentsDisplay
      .filter((attachment) => attachment.is_checked)
      .map((item) => ({
        fileId: item.id,
        isFolder: item.is_folder,
      }));
    openConfirmationModal(
      'Remove Selected Items Confirmation',
      'Please confirm if you want to remove the selected items.',
      'Cancel',
      'Confirm',
      () => {
        deleteFolderAction(filteredAttachments);
      },
    );
  };

  const handleDownload = async () => {
    try {
      const filteredAttachments = attachmentsDisplay.filter(
        (attachment: ReportAttachementProps) =>
          attachment.is_checked && attachment.url,
      );

      const downloadPromises = filteredAttachments.map(
        (attachment: ReportAttachementProps) =>
          downloadAttachment({
            url: attachment.url as string,
            name: attachment.name,
          }),
      );

      await Promise.all(downloadPromises);

      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Successfully downloaded the files.',
        }),
        'success',
      );
    } catch (error) {
      const apiError = error as RESTAPIError;
      createToastNotification(
        apiError.message || 'Something went wrong',
        'error',
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      processFiles([...files]);
    }
  };

  const [isDragging, setIsDragging] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDragEnter = () => {
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (
      parentRef.current &&
      !parentRef.current.contains(event.relatedTarget as Node)
    ) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(false);
    event.preventDefault();
    const files = event.dataTransfer.files;
    processFiles([...files]);
  };

  const handleSetGridSortOption = (value: boolean) => {
    if (value) {
      setAscendingName(true);
      setAscendingDateModified(null);
    } else {
      setAscendingName(null);
      setAscendingDateModified(true);
    }
    setGridViewSortOption(value);
  };

  return (
    <div
      className='my-5 relative'
      ref={parentRef}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Overlay When Dropping a file */}
      <div
        className={clsx([
          'absolute inset-0',
          isDragging ? 'block p-2 z-20' : 'hidden',
        ])}
      >
        <div className='bg-zinc-400/50 rounded-lg flex flex-col h-full items-center justify-center'>
          <span className='size-10'>
            <FileArrowDown className='w-full text-white h-full' />
          </span>
          <Typography
            level='title-md'
            sx={{ color: (theme) => theme.palette.common.white }}
          >
            {intl.formatMessage({
              defaultMessage: 'Drop a file to upload',
            })}
          </Typography>
        </div>
      </div>

      <Typography level='body-md'>
        <FormattedMessage defaultMessage="Upload your file here by simply clicking the 'Choose Files' button" />
      </Typography>

      {/* Buttons for Choose Files and Create Folder */}
      <div className='flex flex-row w-full space-between my-4'>
        <div className='flex flex-row space-x-2 flex-1'>
          <Button
            component='label'
            variant='outlined'
            color='neutral'
            startDecorator={<UploadSimple />}
            className='relative'
          >
            <input
              id='file-upload'
              type='file'
              multiple
              className='absolute inset-0 opacity-0'
              onChange={handleFileChange}
              ref={fileInputRef}
            />
            <FormattedMessage defaultMessage='Choose Files' />
          </Button>

          <CreateFolderModal
            reportId={reportId}
            folderId={directoryPath?.slice(-1)[0].id}
            currentDirectoryItems={attachmentsDisplay}
          />
        </div>

        {/* <Button variant='outlined' color='gradient-primary'>
            <FormattedMessage defaultMessage='Submit' />
          </Button> */}
      </div>

      {/* Folder Directory */}
      <div className='h-[345px] overflow-y-auto'>
        <div className='flex items-center w-full'>
          <Breadcrumbs sx={{ p: 0 }}>
            {directoryPath?.map((folder, index) => (
              <Typography
                component='button'
                color='neutral'
                fontWeight={500}
                className='hover:underline'
                key={index}
                onClick={() => handleNavigate(folder.id)}
              >
                {folder.name}
              </Typography>
            ))}
          </Breadcrumbs>

          {/* Navigation for List view and Grid view & Checkbox for Select All */}
          <div className='ml-auto'>
            <ButtonGroup>
              <IconButton onClick={handleClickCheckAll}>
                {plus ? <PlusSquare /> : <MinusSquare />}
              </IconButton>
              <IconButton onClick={handleClickGridView}>
                {gridView ? (
                  <GridFour />
                ) : (
                  <Columns className='transform rotate-90' />
                )}
              </IconButton>
            </ButtonGroup>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className='flex gap-2'>
          {showBulkUpdateButtons.download && (
            <Button
              variant='outlined'
              color='neutral'
              shape='pill'
              onClick={() => handleDownload()}
              startDecorator={<DownloadSimple />}
            >
              <FormattedMessage defaultMessage='Download' />
            </Button>
          )}

          {showBulkUpdateButtons.remove && (
            <Button
              variant='outlined'
              color='neutral'
              shape='pill'
              startDecorator={<Trash />}
              onClick={() => handleDeleteCheckedItems()}
            >
              <FormattedMessage defaultMessage='Remove' />
            </Button>
          )}

          {showBulkUpdateButtons.move && (
            <MoveFolderModal
              checkedItems={attachmentsDisplay.filter((obj) => obj.is_checked)}
              reportId={reportId}
              attachments={attachments}
              attachmentsDisplay={attachmentsDisplay}
              directoryPath={directoryPath}
            />
          )}
        </div>

        {/* Sorting by Name */}
        {!gridView && (
          <div className='flex items-center w-full pt-2 pb-4'>
            <button
              className='flex items-center gap-1 w-7/10'
              onClick={handleSortByName}
            >
              <Text className='text-neutral-700'>
                <FormattedMessage defaultMessage='Name' />
              </Text>

              {ascendingName ? (
                <ArrowDown size={20} color='#000000' />
              ) : (
                <ArrowUp size={20} color='#000000' />
              )}
            </button>

            <button
              className='flex items-center gap-1 w-3/10 ml-auto mr-20'
              onClick={handleSortByDateModified}
            >
              <Text className='text-neutral-700'>
                <FormattedMessage defaultMessage='Last Modified' />
              </Text>

              {ascendingDateModified ? (
                <ArrowDown size={20} color='#000000' />
              ) : (
                <ArrowUp size={20} color='#000000' />
              )}
            </button>
          </div>
        )}

        {gridView && (
          <div className='py-4'>
            <Dropdown>
              <ButtonGroup>
                <Button
                  endDecorator={
                    <ArrowUp
                      className={clsx(
                        'transition ease-out',
                        getSortPropertyOrder(sortProperty) === 'asc'
                          ? 'rotate-180'
                          : '',
                      )}
                    />
                  }
                  onClick={() => sortBy(sortProperty)}
                >
                  {sortProperty === 'name'
                    ? intl.formatMessage({ defaultMessage: 'Name' })
                    : intl.formatMessage({ defaultMessage: 'Last Modified' })}
                </Button>
                <MenuButton slots={{ root: IconButton }}>
                  <CaretDown />
                </MenuButton>
              </ButtonGroup>
              <Menu placement='bottom-end'>
                <ListSubheader>
                  {intl.formatMessage({ defaultMessage: 'Sort By' })}
                </ListSubheader>
                <Divider />
                <MenuItem
                  onClick={() => handleSetGridSortOption(true)}
                  selected={sortProperty === 'name'}
                >
                  {intl.formatMessage({ defaultMessage: 'Name' })}
                </MenuItem>
                <MenuItem
                  onClick={() => handleSetGridSortOption(false)}
                  selected={sortProperty === 'last_modified'}
                >
                  {intl.formatMessage({ defaultMessage: 'Last Modified' })}
                </MenuItem>
              </Menu>
            </Dropdown>
          </div>
        )}

        {/* Display if no attachments */}
        {attachmentsDisplay?.length === 0 ? (
          <div className='relative flex flex-col py-5 justify-center items-center gap-4 overflow-hidden rounded-md'>
            <img
              src={noAttachmentIcon}
              alt='no-attachment-icon'
              className='w-48 h-40'
            />
            <Text className='text-neutral-500'>
              <FormattedMessage defaultMessage='No attachment files uploaded' />
            </Text>
          </div>
        ) : (
          <div className='w-full h-full'>
            {/* Displaying the attachments */}
            {gridView ? (
              <div className='grid grid-cols-3 lg:grid-cols-5 gap-3'>
                {attachmentsDisplay?.map((i) =>
                  i.is_folder ? (
                    <Card
                      key={i.id}
                      size='sm'
                      sx={{ height: '100%', position: 'relative' }}
                    >
                      <div className='flex flex-col h-full group'>
                        <div className='absolute top-0  inset-x-0 z-10 p-2 flex justify-between items-center'>
                          <span
                            className={clsx([
                              !i.is_checked
                                ? 'opacity-0 group-hover:opacity-100'
                                : '',
                            ])}
                          >
                            <Checkbox
                              checked={i.is_checked || false}
                              onClick={(e) => handleCheckItem(e, i.id)}
                            />
                          </span>
                          <OtherOptionPopoverFolder
                            absolute
                            iconSize={22}
                            reportId={reportId}
                            attachments={attachments}
                            attachmentsDisplay={attachmentsDisplay}
                            directoryPath={directoryPath}
                            itemInformation={i}
                            deleteFolderAction={deleteFolderAction}
                          />
                        </div>
                        <div className='flex-1 flex flex-col relative'>
                          <div className='flex-1'>
                            <span className='h-28 flex items-center justify-center'>
                              <FolderIcon
                                weight='fill'
                                className='h-full w-full text-brand-primary-500'
                              />
                            </span>
                          </div>
                          <div className='flex flex-col'>
                            <div className='inline-flex gap-0.5 items-center'>
                              <Tooltip title={i.name} enterDelay={2000}>
                                <Typography
                                  component='button'
                                  level='title-sm'
                                  onClick={() => handleNavigateFolder(i.id)}
                                  className='whitespace-nowrap truncate min-w-0 hover:underline text-left'
                                >
                                  <span
                                    aria-hidden='true'
                                    className='absolute inset-0'
                                  />
                                  {i.name}
                                </Typography>
                              </Tooltip>
                              <span className='group-hover:flex hidden animate-slideRightAndFade'>
                                <ArrowRight className='size-4' />
                              </span>
                            </div>
                            <Typography level='body-sm' className='capitalize'>
                              {i.file_type}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <Card
                      key={i.id}
                      size='sm'
                      sx={{ height: '100%', position: 'relative' }}
                    >
                      <div className='flex flex-col h-full group'>
                        <div className='absolute top-0 inset-x-0 p-2 flex items-center justify-between'>
                          <span
                            className={clsx([
                              !i.is_checked
                                ? 'opacity-0 group-hover:opacity-100'
                                : '',
                            ])}
                          >
                            <Checkbox
                              checked={i.is_checked || false}
                              onClick={(e) => handleCheckItem(e, i.id)}
                            />
                          </span>
                          <OtherOptionsPopoverFile
                            absolute
                            iconSize={22}
                            reportId={reportId}
                            attachments={attachments}
                            attachmentsDisplay={attachmentsDisplay}
                            directoryPath={directoryPath}
                            itemInformation={i}
                            deleteFolderAction={deleteFolderAction}
                          />
                        </div>
                        <AttachedFileItemDisplay
                          gridView
                          id={i.id}
                          url={i.url}
                          fileType={i.file_type}
                          name={i.name}
                          userType={i.user_type}
                        />
                      </div>
                    </Card>
                  ),
                )}
              </div>
            ) : (
              <div
                className={`list-item gap-1 overflow-hidden ${
                  isDragging
                    ? 'bg-gray-200 border-gray-200'
                    : 'bg-transparent border-transparent'
                }`}
              >
                {attachmentsDisplay?.map((i) =>
                  i.is_folder ? (
                    <button
                      key={i.id}
                      className='relative flex items-center p-2 text-left overflow-hidden shadow-sm w-full hover:bg-gray-100 cursor-pointer group'
                      onClick={() => handleNavigateFolder(i.id)}
                    >
                      <div className='w-full flex items-center justify-between'>
                        <div className='flex items-center'>
                          <button
                            className={`top-3 left-3 mr-3 flex items-center space-x-2 transition-opacity duration-300 ${
                              i.is_checked
                                ? 'opacity-100'
                                : 'opacity-0 group-hover:opacity-100'
                            }`}
                          >
                            <Checkbox
                              size='lg'
                              checked={i.is_checked || false}
                              onClick={(e) => handleCheckItem(e, i.id)}
                              className='border-brand-primary-800 border-2 rounded-md'
                            />
                          </button>

                          <FolderIcon
                            size={40}
                            color='#00cecf'
                            weight='fill'
                            className='mr-4'
                          />

                          <Text className='text-md text-gray-500'>
                            {i.name}
                          </Text>
                        </div>
                        <div className='flex items-center'>
                          <Text className='text-md text-gray-500'>
                            {format(
                              new Date(i.last_modified),
                              'MMMM dd, yyyy',
                            ) || 'N/A'}
                          </Text>
                          <Text className='text-md text-gray-500 ml-4'>
                            {i.uploader_name}
                          </Text>

                          <OtherOptionPopoverFolder
                            iconSize={22}
                            reportId={reportId}
                            attachments={attachments}
                            attachmentsDisplay={attachmentsDisplay}
                            directoryPath={directoryPath}
                            itemInformation={i}
                            deleteFolderAction={deleteFolderAction}
                          />
                        </div>
                      </div>
                    </button>
                  ) : (
                    <div
                      key={i.id}
                      className='relative flex items-center justify-between p-2 text-left overflow-hidden shadow-sm w-full hover:bg-gray-100 cursor-pointer group'
                    >
                      <div className='flex flex-row grow gap-1 items-center'>
                        <Checkbox
                          size='lg'
                          checked={i.is_checked || false}
                          onClick={(e) => handleCheckItem(e, i.id)}
                          className={`border-brand-primary-800 border-2 rounded-md ${
                            i.is_checked
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100'
                          }`}
                        />
                        <AttachedFileItemDisplay
                          id={i.id}
                          url={i.url}
                          fileType={i.file_type}
                          name={i.name}
                          userType={i.user_type}
                        />
                      </div>
                      <div className='flex items-center'>
                        <Text className='text-md text-gray-500'>
                          {format(new Date(i.last_modified), 'MMMM dd, yyyy') ||
                            'N/A'}
                        </Text>
                        <Text className='text-md text-gray-500 ml-4'>
                          {i.uploader_name ? i.uploader_name : 'Unknown'}
                        </Text>

                        <OtherOptionsPopoverFile
                          iconSize={22}
                          reportId={reportId}
                          attachments={attachments}
                          attachmentsDisplay={attachmentsDisplay}
                          directoryPath={directoryPath}
                          itemInformation={i}
                          deleteFolderAction={deleteFolderAction}
                        />
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div
        className={clsx(
          'absolute z-10 inset-0 flex justify-center items-center rounded-[5px] ',
          isLoadingUpload ? 'block' : 'hidden',
        )}
      >
        <CircularProgress variant='solid' color='primary' />
      </div>
    </div>
  );
}
